import type {
  ColumnPinningPosition,
  ColumnPinningState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { type JSX, useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';


import type { ColumnListItem } from './column-settings-types.js';
import {
  generateColumnLabelsMap,
  getVisibilityCheckboxState,
  groupColumns,
  isParentColumnHideable,
  processFilterValue,
} from './column-settings-utils.js';
import { DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT } from './ColumnOrderSettings/column-order-settings-dnd-utils.js';
import { ColumnOrderSettingsDragAndDropContext } from './ColumnOrderSettings/ColumnOrderSettingsDragAndDrop.js';
import * as styles from './DataTableColumnsList.sty.js';
import { columnName } from './DataTableColumnsListItem.sty.js';
import { DataTableColumnsListItem } from './DataTableColumnsListItem.js';
import { Grid } from '../../../../../layouts/grid/Grid.js';
import { Text } from '../../../../../typography/text/Text.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import { getCanHide } from '../../../features/ColumnVisibility/ColumnVisibility.js';
import type { Column } from '../../../hooks/useTable/types.js';
import { dataTableMessages } from '../../../messages.js';

interface DataTableColumnsListProps {
  /**
   * Whether the visibility settings (checkboxes) are enabled.
   */
  visibilitySettings: boolean;
  /**
   * Whether the column order settings (drag and drop) are enabled.
   */
  columnOrderSettings: boolean;
  /**
   * Whether the column pinning settings (pin/unpin buttons) are enabled.
   */
  columnPinningSettings: boolean;
  /**
   * Current value of the filter input in the column settings modal.
   */
  activeFilterValue: string;
  /**
   * Current local column visibility state in the column settings modal.
   */
  localColumnVisibility: Record<string, boolean>;
  /**
   * Updates the local column visibility state in the column settings modal.
   * @param column - the column to update.
   * @param currentVisibility - the current visibility state of the column.
   */
  updateLocalColumnVisibility: (
    column: Column<unknown, unknown>,
    currentVisibility: boolean | 'indeterminate',
  ) => void;
  /**
   * Current local column order in the column settings modal.
   */
  localColumnOrder: string[];
  /**
   * Updates the local column order in the column settings modal.
   * @param newColumnOrder - the new column order.
   */
  updateLocalColumnOrder: (newColumnOrder: string[]) => void;
  /**
   * Current local column pinning state in the column settings modal.
   */
  localColumnPinning: ColumnPinningState;
  /**
   * Updates the local column pinning state in the column settings modal.
   * @param newState - the new column pinning state.
   */
  updateLocalColumnPinning: (newState: ColumnPinningState) => void;
  /**
   * Whether the filter bar is present in the modal above the list.
   */
  hasFilterBar: boolean;
}

/**
 * Renders the ordered, filtered list of columns for the column settings modal.
 * @internal
 */
export const DataTableColumnsList = (
  props: DataTableColumnsListProps,
): JSX.Element | JSX.Element[] => {
  const {
    visibilitySettings: showVisibilityCheckbox,
    columnOrderSettings,
    columnPinningSettings,
    activeFilterValue,
    localColumnVisibility,
    updateLocalColumnVisibility,
    localColumnOrder,
    updateLocalColumnOrder,
    localColumnPinning,
    updateLocalColumnPinning,
    hasFilterBar,
  } = props;
  const table = useDataTable();
  const intl = useIntl();
  const parentRef = useRef<HTMLDivElement | null>(null);

  const allLeafColumnsWithoutBuiltin = table.getAllLeafColumnsWithoutBuiltin();
  const columnsById = useMemo(
    () => new Map(allLeafColumnsWithoutBuiltin.map((col) => [col.id, col])),
    [allLeafColumnsWithoutBuiltin],
  );

  // Memoize column labels to avoid repeated getColumnLabel calls during filtering
  const columnLabelsById = useMemo(
    () => generateColumnLabelsMap(allLeafColumnsWithoutBuiltin),
    [allLeafColumnsWithoutBuiltin],
  );

  // Pre-process filter value to lowercase words once
  const activeFilterWords = useMemo(
    () => processFilterValue(activeFilterValue),
    [activeFilterValue],
  );

  // order the columns using the local column order
  const compareColumns = useCallback(
    (a: Column<unknown, unknown>, b: Column<unknown, unknown>) => {
      const idChildColumnA = a.columns.at(0)?.id;
      const idChildColumnB = b.columns.at(0)?.id;
      const isBChildOfA = !!a.columns.find((childCol) => childCol.id === b.id);
      const isAChildOfB = !!b.columns.find((childCol) => childCol.id === a.id);

      if (isBChildOfA) {
        return -1;
      } else if (isAChildOfB) {
        return 1;
      }

      return (
        localColumnOrder.indexOf(idChildColumnA ?? a.id) -
        localColumnOrder.indexOf(idChildColumnB ?? b.id)
      );
    },
    [localColumnOrder],
  );

  // filter the flat columns using the active filter value specified in the search bar
  const filterColumns = useCallback(
    (
      column: ColumnListItem,
      _: number,
      columnSection: ColumnListItem[],
    ): boolean => {
      // Early return if no filter
      if (activeFilterWords.length === 0) {
        return true;
      }

      // Helper function to check if a column matches the filter words
      const matchesFilter = (columnId: string): boolean => {
        const columnLabel = columnLabelsById.get(columnId) ?? '';
        return activeFilterWords.every((word) => columnLabel.includes(word));
      };

      const childColumns = column.columns;
      if (childColumns.length === 0) {
        return matchesFilter(column.id);
      }

      return (
        childColumns
          // only consider children part of the current (un)pinned section
          .filter((child) => columnSection.find((col) => col.id === child.id))
          .some((childColumn) => matchesFilter(childColumn.id))
      );
    },
    [activeFilterWords, columnLabelsById],
  );

  const mapToListItem = (
    col: Column<unknown, unknown>,
    idx: number,
    pinnedPosition: ColumnPinningPosition,
  ): ColumnListItem => ({
    ...col,
    key: `${col.id}_${idx}_${pinnedPosition}`, // unique React key; parent columns may appear multiple times in list
    pinned: pinnedPosition,
  });

  const orderedLeftFlatColumns: ColumnListItem[] = useMemo(() => {
    // map column IDs to columns
    const localColumnsPinned =
      localColumnPinning.left
        ?.map((id) => columnsById.get(id))
        .filter((column) => !!column) ?? [];

    // group nested columns
    const groupedColumns = groupColumns(localColumnsPinned);

    // add unique key
    return groupedColumns.map((col, idx) => mapToListItem(col, idx, 'left'));
  }, [columnsById, localColumnPinning.left]);

  const orderedCenterFlatColumns: ColumnListItem[] = useMemo(() => {
    // find unpinned columns
    const localColumnsUnpinned = allLeafColumnsWithoutBuiltin.filter(
      (column) =>
        !localColumnPinning.left?.includes(column.id) &&
        !localColumnPinning.right?.includes(column.id),
    );

    // group nested columns
    const groupedColumns = groupColumns(localColumnsUnpinned);

    // order columns and add unique key
    return groupedColumns
      .toSorted(compareColumns)
      .map((col, idx) => mapToListItem(col, idx, false));
  }, [
    allLeafColumnsWithoutBuiltin,
    compareColumns,
    localColumnPinning.left,
    localColumnPinning.right,
  ]);

  const orderedRightFlatColumns: ColumnListItem[] = useMemo(() => {
    // map column IDs to columns
    const localColumnsPinned =
      localColumnPinning.right
        ?.map((id) => columnsById.get(id))
        .filter((column) => !!column) ?? [];

    // group nested columns
    const groupedColumns = groupColumns(localColumnsPinned);

    // add unique key
    return groupedColumns.map((col, idx) => mapToListItem(col, idx, 'right'));
  }, [columnsById, localColumnPinning.right]);

  const orderedFlatColumns = {
    left: orderedLeftFlatColumns,
    center: orderedCenterFlatColumns,
    right: orderedRightFlatColumns,
  };

  const filteredColumns = useMemo(
    () => [
      ...orderedLeftFlatColumns.filter(filterColumns),
      ...orderedCenterFlatColumns.filter(filterColumns),
      ...orderedRightFlatColumns.filter(filterColumns),
    ],
    [
      filterColumns,
      orderedCenterFlatColumns,
      orderedLeftFlatColumns,
      orderedRightFlatColumns,
    ],
  );

  const getItemKey = useCallback(
    (index: number) => filteredColumns[index].key,
    [filteredColumns],
  );

  const columnVirtualizer = useVirtualizer({
    count: filteredColumns.length,
    overscan: 5,
    getScrollElement: () => parentRef.current,
    getItemKey,
    // Approximate height of a column list item
    estimateSize: () => DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT,
    measureElement: (element) => {
      if (!element || !(element instanceof HTMLElement)) {
        return DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT;
      }

      const labelElement = element.querySelector(`.${columnName}`);
      if (!labelElement) {
        return DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT;
      }

      const computedStyle = getComputedStyle(element);
      const borderTop = parseFloat(computedStyle.borderTopWidth);
      const borderBottom = parseFloat(computedStyle.borderBottomWidth);

      return (
        labelElement.getBoundingClientRect().height +
        2 * 4 + // padding top and bottom
        borderTop +
        borderBottom
      );
    },
  });

  const virtualItems = columnVirtualizer.getVirtualItems();

  // fallback message if no columns match the filter
  if (filteredColumns.length === 0) {
    return (
      <Text className={styles.noItemsFound}>
        {intl.formatMessage(dataTableMessages.noItemsFound)}
      </Text>
    );
  }

  const showColumnPinningButton =
    !!table.options.enableColumnPinning && columnPinningSettings;
  const canOrder =
    !!table.options.enableColumnOrdering &&
    columnOrderSettings &&
    activeFilterValue.length === 0;

  const columnListItems = virtualItems.map((virtualItem) => {
    const column = filteredColumns[virtualItem.index];
    const columnKey = column.key;
    const canHide = getCanHide(table, column, isParentColumnHideable);
    const visibilityCheckboxState = getVisibilityCheckboxState(
      localColumnVisibility,
      column,
    );
    const showColumnOrderDragHandle = canOrder && column.pinned === false;

    return (
      <DataTableColumnsListItem
        key={columnKey}
        column={column}
        columnIndex={localColumnOrder.indexOf(column.id)}
        orderedFlatColumns={orderedFlatColumns}
        showVisibilityCheckbox={showVisibilityCheckbox}
        enableVisibilityCheckbox={canHide}
        currentVisibilityState={visibilityCheckboxState}
        onVisibilityChange={updateLocalColumnVisibility}
        showColumnOrderDragHandle={showColumnOrderDragHandle}
        showColumnPinningMenu={showColumnPinningButton}
        localColumnPinning={localColumnPinning}
        updateLocalColumnPinning={updateLocalColumnPinning}
        virtualIndex={virtualItem.index}
        measureElement={columnVirtualizer.measureElement}
      />
    );
  });

  const columnList = (
    <div
      ref={parentRef}
      className={clsx(
        'strato-table-settings-virtualization-container',
        styles.virtualizationScrollContainer({
          hasFilter: hasFilterBar,
        }),
      )}
    >
      <Grid
        gridTemplateColumns="1fr"
        padding={0}
        gap={0}
        className={clsx(styles.virtualizationInnerContainer)}
        gridTemplateRows={columnVirtualizer.measurementsCache
          .map((el) => `${el.size}px`)
          .join(' ')}
        // add 1px to prevent unwanted scrollbar during drag and drop
        height={columnVirtualizer.getTotalSize() + 1}
      >
        {columnListItems}
      </Grid>
    </div>
  );

  return canOrder ? (
    <ColumnOrderSettingsDragAndDropContext
      localColumnOrder={localColumnOrder}
      updateLocalColumnOrder={updateLocalColumnOrder}
      localColumnPinning={localColumnPinning}
      itemVirtualizer={columnVirtualizer}
    >
      {columnList}
    </ColumnOrderSettingsDragAndDropContext>
  ) : (
    columnList
  );
};
